import React from 'react';

import { SegmentedControl, SegmentedControlProps } from '@mantine/core';
import { WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';

// Ascending difficulty (Scaled -> RX -> RX+) — a display convention, independent of
// the RX -> Scaled -> RX+ order used to pick a default level (see getDefaultLevel).
const DISPLAY_ORDER = [WorkoutLevelEnum.SCALED, WorkoutLevelEnum.RX, WorkoutLevelEnum.RX_PLUS];

export interface LevelPickerProps extends Omit<SegmentedControlProps, 'value' | 'onChange' | 'data'> {
  value: WorkoutLevelEnum | null;
  onChange: (value: WorkoutLevelEnum) => void;
  levelScaled: boolean;
  levelRx: boolean;
  levelRxPlus: boolean;
}

const LEVEL_AVAILABILITY: Record<
  WorkoutLevelEnum,
  keyof Pick<LevelPickerProps, 'levelScaled' | 'levelRx' | 'levelRxPlus'>
> = {
  [WorkoutLevelEnum.SCALED]: 'levelScaled',
  [WorkoutLevelEnum.RX]: 'levelRx',
  [WorkoutLevelEnum.RX_PLUS]: 'levelRxPlus',
};

const LevelPicker = ({
  value,
  onChange,
  levelScaled,
  levelRx,
  levelRxPlus,
  ...segmentedControlProps
}: LevelPickerProps): JSX.Element | null => {
  const availability = { levelScaled, levelRx, levelRxPlus };
  const data = DISPLAY_ORDER.filter(level => availability[LEVEL_AVAILABILITY[level]]).map(level => ({
    value: level,
    label: WORKOUT_LEVEL_LABEL_MAP[level],
  }));

  if (data.length === 0) return null;

  return (
    <SegmentedControl
      {...segmentedControlProps}
      data={data}
      value={value ?? ''}
      onChange={v => onChange(v as WorkoutLevelEnum)}
    />
  );
};

export default LevelPicker;
